import { useState, useEffect } from 'react';
import { Shield, Play, CheckCircle, XCircle, AlertTriangle, Eye, RefreshCw, Users, Clock, Activity, FileWarning } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SecurityTestResult {
  testName: string;
  passed: boolean;
  message: string;
  details?: any;
}

interface AuditLog {
  id: string;
  user_id: string;
  impersonated_user_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: any;
  created_at: string;
}

interface ImpersonationSession {
  id: string;
  admin_user_id: string;
  impersonated_user_id: string;
  started_at: string;
  ended_at: string | null;
  is_active: boolean;
}

export function SecurityManagement() {
  const [testResults, setTestResults] = useState<SecurityTestResult[]>([]);
  const [testLoading, setTestLoading] = useState(false);
  const [testSummary, setTestSummary] = useState<{ total: number; passed: number; failed: number } | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [impersonationSessions, setImpersonationSessions] = useState<ImpersonationSession[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [activeView, setActiveView] = useState<'tests' | 'audit' | 'sessions'>('tests');

  useEffect(() => {
    if (activeView === 'audit') {
      fetchAuditLogs();
    } else if (activeView === 'sessions') {
      fetchImpersonationSessions();
    }
  }, [activeView]);

  async function runSecurityTests() {
    setTestLoading(true);
    setTestResults([]);
    setTestSummary(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-security-tests`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'exécution des tests');
      }

      const data = await response.json();
      setTestResults(data.results || []);
      setTestSummary(data.summary || null);
    } catch (error) {
      console.error('Error running security tests:', error);
      alert('Erreur lors de l\'exécution des tests de sécurité');
    } finally {
      setTestLoading(false);
    }
  }

  async function fetchAuditLogs() {
    setLogsLoading(true);
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setAuditLogs(data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }

  async function fetchImpersonationSessions() {
    setSessionsLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_impersonation_sessions')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setImpersonationSessions(data || []);
    } catch (error) {
      console.error('Error fetching impersonation sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  }

  function getActionBadge(action: string) {
    switch (action) {
      case 'impersonate_start':
        return <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded">Impersonation démarrée</span>;
      case 'impersonate_end':
        return <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded">Impersonation terminée</span>;
      case 'security_alert':
        return <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded">Alerte sécurité</span>;
      case 'login':
        return <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded">Connexion</span>;
      case 'logout':
        return <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded">Déconnexion</span>;
      case 'failed_login':
        return <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded">Connexion échouée</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded">{action}</span>;
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="text-blue-600" size={32} />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Outils de Sécurité</h2>
            <p className="text-sm text-gray-600">Tests, logs d'audit et monitoring des sessions d'impersonation</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveView('tests')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeView === 'tests'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Play size={16} />
          Tests de sécurité
        </button>
        <button
          onClick={() => setActiveView('audit')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeView === 'audit'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Eye size={16} />
          Logs d'audit
        </button>
        <button
          onClick={() => setActiveView('sessions')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeView === 'sessions'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Users size={16} />
          Sessions d'impersonation
        </button>
      </div>

      {activeView === 'tests' && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Tests de Sécurité Automatisés</h3>
                <p className="text-sm text-blue-800 mb-3">
                  Ces tests vérifient l'isolation des données, les policies RLS, le système d'impersonation et les logs d'audit.
                  Recommandation: Exécuter mensuellement.
                </p>
                <button
                  onClick={runSecurityTests}
                  disabled={testLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {testLoading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Exécution en cours...
                    </>
                  ) : (
                    <>
                      <Play size={16} />
                      Lancer les tests
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {testSummary && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-3xl font-bold text-gray-900">{testSummary.total}</p>
                  </div>
                  <Activity className="text-gray-400" size={32} />
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Réussis</p>
                    <p className="text-3xl font-bold text-green-900">{testSummary.passed}</p>
                  </div>
                  <CheckCircle className="text-green-400" size={32} />
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Échoués</p>
                    <p className="text-3xl font-bold text-red-900">{testSummary.failed}</p>
                  </div>
                  <XCircle className="text-red-400" size={32} />
                </div>
              </div>
            </div>
          )}

          {testResults.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Résultats des Tests</h3>
              {testResults.map((result, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 ${
                    result.passed
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {result.passed ? (
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                    ) : (
                      <XCircle className="text-red-600 flex-shrink-0 mt-1" size={20} />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-semibold ${
                        result.passed ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {result.testName}
                      </h4>
                      <p className={`text-sm mt-1 ${
                        result.passed ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {result.message}
                      </p>
                      {result.details && (
                        <pre className="mt-2 text-xs bg-white bg-opacity-50 rounded p-2 overflow-x-auto">
                          {JSON.stringify(result.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeView === 'audit' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Derniers Logs d'Audit (50 plus récents)</h3>
            <button
              onClick={fetchAuditLogs}
              disabled={logsLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={14} className={logsLoading ? 'animate-spin' : ''} />
              Actualiser
            </button>
          </div>

          {logsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-gray-400" size={32} />
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileWarning size={48} className="mx-auto mb-4 text-gray-400" />
              <p>Aucun log d'audit trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Utilisateur</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Impersonation</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ressource</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Détails</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(log.created_at).toLocaleString('fr-FR')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getActionBadge(log.action)}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-xs text-gray-600">
                        {log.user_id.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {log.impersonated_user_id ? (
                          <span className="font-mono text-xs text-blue-600">
                            {log.impersonated_user_id.substring(0, 8)}...
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.resource_type || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {log.details ? (
                          <details className="cursor-pointer">
                            <summary className="text-blue-600 hover:text-blue-700">Voir</summary>
                            <pre className="mt-2 text-xs bg-gray-100 rounded p-2 overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeView === 'sessions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Sessions d'Impersonation (50 plus récentes)</h3>
            <button
              onClick={fetchImpersonationSessions}
              disabled={sessionsLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={14} className={sessionsLoading ? 'animate-spin' : ''} />
              Actualiser
            </button>
          </div>

          {sessionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-gray-400" size={32} />
            </div>
          ) : impersonationSessions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users size={48} className="mx-auto mb-4 text-gray-400" />
              <p>Aucune session d'impersonation trouvée</p>
            </div>
          ) : (
            <div className="space-y-3">
              {impersonationSessions.map((session) => (
                <div
                  key={session.id}
                  className={`border rounded-lg p-4 ${
                    session.is_active
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {session.is_active ? (
                          <span className="px-2 py-1 text-xs font-semibold bg-blue-600 text-white rounded">
                            ACTIVE
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold bg-gray-400 text-white rounded">
                            TERMINÉE
                          </span>
                        )}
                        <span className="text-sm text-gray-600">
                          Session {session.id.substring(0, 8)}...
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Admin</p>
                          <p className="font-mono text-xs text-gray-900">
                            {session.admin_user_id.substring(0, 16)}...
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Utilisateur impersonné</p>
                          <p className="font-mono text-xs text-blue-600">
                            {session.impersonated_user_id.substring(0, 16)}...
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Démarrée</p>
                          <p className="text-gray-900 flex items-center gap-1">
                            <Clock size={14} />
                            {new Date(session.started_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Terminée</p>
                          <p className="text-gray-900 flex items-center gap-1">
                            <Clock size={14} />
                            {session.ended_at ? new Date(session.ended_at).toLocaleString('fr-FR') : '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
